import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton, Badge, Tooltip, Popover, Box, Typography, Stack, Divider, CircularProgress,
} from '@mui/material';
import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';

/**
 * In-app notification centre (top-bar bell + unread badge + dropdown).
 *
 * Lists recent platform activity (new users, products, businesses, leads, RFQs,
 * help/feedback, paid plans) stored by the backend adminNotifier. Distinct from
 * the neighbouring NotificationBell, which controls OS/PWA push permission.
 *
 * - Polls the lightweight unread-count endpoint every 60s for the badge.
 * - Loads the full list only when opened.
 * - Clicking an item marks it read and deep-links to the relevant admin page.
 */
const timeAgo = (d) => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!localStorage.getItem('admin_token')) return;
    try {
      const { data } = await axios.get('/admin/notifications/unread-count', { skipNotify: true });
      setUnread(data.unread || 0);
    } catch { /* silent — badge just won't update */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/admin/notifications', { params: { limit: 30 }, skipNotify: true });
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* notified globally on hard errors */ } finally {
      setLoading(false);
    }
  }, []);

  // Poll the badge count every 60s (lightweight), plus once on mount.
  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => clearInterval(id);
  }, [fetchCount]);

  const open = (e) => { setAnchor(e.currentTarget); fetchList(); };
  const close = () => setAnchor(null);

  const markAll = async () => {
    try {
      await axios.put('/admin/notifications/read-all', {}, { skipNotify: true });
      setItems((xs) => xs.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const clearAll = async () => {
    try {
      await axios.delete('/admin/notifications', { skipNotify: true });
      setItems([]);
      setUnread(0);
    } catch { /* ignore */ }
  };

  const onItem = (n) => {
    close();
    if (!n.read) {
      axios.put(`/admin/notifications/${n._id}/read`, {}, { skipNotify: true }).catch(() => {});
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.url) navigate(n.url);
  };

  return (
    <>
      <Tooltip title="Activity">
        <IconButton onClick={open} sx={{ color: 'text.secondary' }} aria-label="Activity notifications">
          <Badge badgeContent={unread} color="error" max={99}>
            <Bell size={20} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: '92vw', borderRadius: 3, mt: 1 } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 800 }}>Notifications</Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Mark all read">
              <span><IconButton size="small" onClick={markAll} disabled={!unread}><CheckCheck size={17} /></IconButton></span>
            </Tooltip>
            <Tooltip title="Clear all">
              <span><IconButton size="small" onClick={clearAll} disabled={!items.length}><Trash2 size={16} /></IconButton></span>
            </Tooltip>
          </Stack>
        </Stack>
        <Divider />

        <Box sx={{ maxHeight: 440, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={22} /></Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              No notifications yet
            </Typography>
          ) : items.map((n) => (
            <Box
              key={n._id}
              onClick={() => onItem(n)}
              sx={{
                px: 2, py: 1.25, cursor: 'pointer',
                bgcolor: n.read ? 'transparent' : 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                borderBottom: '1px solid', borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ mt: 0.9, width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: n.read ? 'transparent' : 'error.main' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.read ? 600 : 800 }} noWrap>{n.title}</Typography>
                  {n.body && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{n.body}</Typography>}
                  <Typography variant="caption" color="text.disabled">{timeAgo(n.created_at)}</Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter;
